import type { TransactionDetail } from '@/types';

type CouponUsed = NonNullable<TransactionDetail['coupon_used']>;

/**
 * Formats a used coupon for the transaction details drawer, e.g.
 * "Summer2024BTC - 15% Off Fees". No coupon (null/undefined) -> "N/A".
 *
 * discount_value for a percentage coupon comes across as a 0-1 fraction (0.2 for a
 * 20% coupon), not a plain "20" - confirmed against real coupon data (was previously
 * assumed to be a plain display number, which showed e.g. "0.2% Off Fees" instead of
 * "20% Off Fees"). A value <= 1 is treated as that fraction and scaled up to a percent;
 * anything larger is already a plain percentage and is left as-is.
 */
export const formatCouponUsed = (coupon?: CouponUsed | null): string => {
  if (!coupon?.code) {
    return 'N/A';
  }

  const discount = formatCouponDiscount(coupon);
  return discount ? `${coupon.code} - ${discount}` : coupon.code;
};

/**
 * Formats just the discount portion of a used coupon, e.g. "15% Off Fees". Empty string
 * when there's no discount_value to describe (a code-only coupon, or no coupon at all).
 */
export const formatCouponDiscount = (coupon?: CouponUsed | null): string => {
  const { discount_value, discount_type } = coupon || {};

  if (!discount_value) {
    return '';
  }

  return discount_type === 'percentage'
    ? `${formatPercentageDiscountValue(discount_value)}% Off Fees`
    : discount_type === 'fixed' || discount_type === 'amount'
      ? `$${discount_value} Off Fees`
      : `${discount_value}${discount_type ? ` ${discount_type}` : ''} Off Fees`;
};

const formatPercentageDiscountValue = (discountValue: string): string => {
  const numericValue = Number(discountValue);
  if (Number.isNaN(numericValue)) {
    return discountValue;
  }

  const percentValue = numericValue > 0 && numericValue <= 1 ? numericValue * 100 : numericValue;
  // Round to avoid float noise (e.g. 0.29 * 100) without truncating a genuine decimal percent.
  return String(Math.round(percentValue * 100) / 100);
};
